<template>
  <div class="map">
    <div ref="container" class="map-container"></div>
    <div class="data-labels d-flex justify-center">
      <div class="me-16">
        <p class="ma-0">{{ $t('insights.no_data') }}</p>
        <v-sheet :color="noDataColor" height="13" width="57"></v-sheet>
      </div>
      <div>
        <template v-for="(value, index) in rangeValues">
          <p :key="value + index" class="data-range ma-0 d-inline-block">
            {{ value }}
          </p>
        </template>
        <div class="d-flex">
          <template v-for="(item, index) in colorScaleQty">
            <v-sheet
              :key="index"
              :color="colorsScale[index]"
              height="13"
              width="89"
            ></v-sheet>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as Datamap from 'datamaps'
import * as d3 from 'd3'
import chroma from 'chroma-js'
import _ from 'lodash'
import localesDictionary from '@/locales/dictionary.json'
import currencies from '@/locales/currencies.json'

import countryCodes from '@/lib/countryCodes.js'

const BASE_COLOR = '#B3DFC1'
const TOP_COLOR = '#2A316C'
const HIGHLIGHT_COLOR = '#FF5B44'
const DEFAULT_NO_DATA_COLOR = '#E2E8F0'
const DEFAULT_SCALE_QUANTITY = 5
const DEFAULT_MAX_SCALE = 50

export default {
  name: 'GlobalMap',

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      countryCodes: countryCodes,
      primaryDialects: localesDictionary['primary-dialects'],
      noDataColor: DEFAULT_NO_DATA_COLOR,
      baseColorPattern: BASE_COLOR,
      topColorPattern: TOP_COLOR,
      colorScaleQty: DEFAULT_SCALE_QUANTITY,
      currencyAbbreviations: [
        { value: 1e12, symbol: 'T' },
        { value: 1e9, symbol: 'B' },
        { value: 1e6, symbol: 'M' },
        { value: 1e3, symbol: 'k' },
      ],
    }
  },

  watch: {
    series: {
      deep: true,
      handler(newValue, oldValue) {
        if (!_.isEqual(newValue, oldValue)) {
          // make sure that the map re-render after a minimal change
          this.drawMap()
        }
      },
    },
  },

  mounted() {
    this.drawMap()
  },

  computed: {
    ...mapGetters(['organization']),

    series() {
      return this.data?.series ?? []
    },

    label() {
      const label = this.data?.label ?? ''
      if (label.length) return label + ': '
      return label
    },

    isEconomicValue() {
      return this.data?.isEconomicValue ?? false
    },

    organizationLanguage() {
      return this.organization.language
    },

    organizationCurrency() {
      return this.organization.currency
    },

    currencySymbol() {
      const currencyData = currencies[this.organizationCurrency]
      return currencyData.symbol_native
    },

    colorsScale() {
      return chroma
        .scale([this.baseColorPattern, this.topColorPattern])
        .mode('lch')
        .colors(this.colorScaleQty)
    },

    maxRangeValue() {
      const onlyValues = this.series.map(item => {
        return item[1]
      })
      let maxValue = Math.max.apply(null, onlyValues)
      if (maxValue < DEFAULT_MAX_SCALE) {
        maxValue = DEFAULT_MAX_SCALE
      }
      return this.roundToNextTen(maxValue)
    },

    rangeValues() {
      const multiplicador = Math.round(this.maxRangeValue / this.colorScaleQty)
      let values = this.getRangeValues(multiplicador, this.colorScaleQty)
      if (this.isEconomicValue) {
        values = values.map(value => this.currencySymbol + value)
      }
      return values
    },

    mapDataset() {
      const dataset = {}
      const paletteScale = d3
        .scaleLinear()
        .domain([0, this.maxRangeValue])
        .range([this.baseColorPattern, this.topColorPattern])
        .interpolate(d3.interpolateCubehelix.gamma(1))

      this.series.forEach(item => {
        // item example ['US', 50]
        const alpha2Code = item[0]
        const alpha3Code = this.getAlphaCode(alpha2Code, 'alpha3')
        const value = item[1]
        dataset[alpha3Code] = {
          value: value,
          countryCode: alpha2Code,
          fillColor: paletteScale(value),
        }
      })
      return dataset
    },
  },

  methods: {
    getRangeValues(multiplier, range) {
      let rangeToIterate = 0
      const values = [0]

      while (rangeToIterate < range - 1) {
        const newValue = values[rangeToIterate] + multiplier
        values.push(newValue)
        rangeToIterate = ++rangeToIterate
      }
      values.push(this.maxRangeValue)
      const bigNumbersFormatted = values.map(value =>
        this.abbreviateIfBigNumber(value)
      )
      return bigNumbersFormatted
    },

    abbreviateIfBigNumber(num) {
      // this method abbreviate the big currency numbers
      // only used for economic values
      // showing 1M instead of 1000000
      // for now it only supports english formatting
      const abbreviations = this.currencyAbbreviations

      for (let i = 0; i < abbreviations.length; i++) {
        if (num >= abbreviations[i].value) {
          return num / abbreviations[i].value + abbreviations[i].symbol
        }
      }
      return num.toString()
    },

    roundToNextTen(number) {
      const numberLength = number.toString().length
      let numberToRound = '1'
      while (numberToRound.toString().length < numberLength) {
        numberToRound = numberToRound + '0'
      }
      return Math.ceil(number / numberToRound) * numberToRound
    },

    getAlphaCode(code, type) {
      const keyToMatch = `alpha${code.length}`
      const codeMatch = this.countryCodes.find(c => c[keyToMatch] === code)
      return codeMatch[type]
    },

    getNaturalEarthProjection() {
      const projection = d3.geoNaturalEarth1()
      const path = d3.geoPath().projection(projection)
      return { path: path, projection: projection }
    },

    getFormattedToCurrency(value) {
      const language = this.primaryDialects[this.organizationLanguage]
      return value.toLocaleString(language, {
        style: 'currency',
        currency: this.organizationCurrency,
        currencyDisplay: 'narrowSymbol',
        maximumSignificantDigits: value.toString().length,
      })
    },

    getPopupTemplate(geo, data) {
      if (!data) {
        const countryCode = this.getAlphaCode(geo.id, 'alpha2')
        return `<div class="map-hover-info elevation-4 pa-1">${this.$t(
          `countries.${countryCode}`
        )}</div>`
      }
      return [
        '<div class="map-hover-info elevation-4 pa-2">',
        '<strong>',
        this.$t(`countries.${data.countryCode}`),
        '</strong><br>',
        this.label,
        this.isEconomicValue
          ? this.getFormattedToCurrency(data.value)
          : data.value,
        '</div>',
      ].join('')
    },

    drawMap() {
      const container = this.$refs.container
      container.innerHTML = ''

      new Datamap({
        element: container,
        data: this.mapDataset,
        fills: {
          defaultFill: DEFAULT_NO_DATA_COLOR,
        },
        setProjection: this.getNaturalEarthProjection,
        geographyConfig: {
          hideAntarctica: true,
          popupTemplate: this.getPopupTemplate,
          popupOnHover: true,
          highlightOnHover: true,
          highlightFillColor: HIGHLIGHT_COLOR,
          highlightBorderWidth: 1,
        },
        responsive: true,
      })
    },
  },
}
</script>

<style lang="scss">
.map-hover-info {
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: $color-blue-grey-darken3;
  background-color: $color-white;
}
</style>

<style lang="scss" scoped>
.map {
  width: 100%;
  height: 80%;
  min-height: 350px;
  margin: auto;
}

.map-container {
  position: relative;
  margin: auto;
  padding-bottom: 52% !important;
}

.data-range {
  width: 86px;
}
</style>
